//------- import libraries
import moment from "moment";
import { connect } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { useDebouncedCallback } from "use-debounce";
import React, { useEffect, useMemo, useRef, useState } from "react";

//------- import custom reuseable components
import {
  Topbar,
  Button,
  SearchField,
  TableCustomHeader,
  CategoriesDropdown,
  DashboardDatePicker,
  ServerPaginationTable,
  CustomCellRendererWithSkeleton,
} from "../../../shared/components";

//------- import constants
import {
  EMPTY_STRING,
  skeletonArray,
  globalConstants,
  shopifyConstants,
  defaultGridState,
  dashboardConstants,
  datepickerConstants,
  categoryDropdownConstants,
  TABLE_IDS,
  getShopifyOrderPaymentStatusOptions,
  getShopifyOrderFulfillmentStatusOptions,
} from "../../../constants";

//------ import system enums
import { PRODUCT_TYPES_ENUMS } from "../../../system/globalEnums";

//------- import icons
import {
  ShowIcon,
  CrossIcon,
  PrintIcon,
  SearchIcon,
  BlueBoxIcon,
  ActionDotsIcons,
} from "../../../assets/icons/iconsProvider";

//------ import utility functions
import {
  getRowId,
  getRowStyle,
  getTimeFrame,
  isButtonDisable,
  isSpinnerEnabled,
  LoadingIndicator,
  getReceiptPrintDate,
  getShopifyOrderActions,
  getTotalLineProductsAndQuantity,
  prepareFilterForShopifyOrdersCall,
  getShopifyOrderFulfillmentStatus,
  handlePrintShopifyPackingSlips,
  handleMarkOrderAsOpen,
  handleMarkOrderAsReady,
  handleMarkOrderAsFulfilled,
  handleIsShopifyOrderCancelled,
  handleTransferToLocation,
} from "../../../shared/utility";

//------ import actions
import { shopifyActions } from "../../../redux/actions";

import OrderDetailsModal from "./OrderDetailsModal/OrderDetailsModal";
import DropdownSkeleton from "../../../shared/Skeletons/DropdownSkeleton/DropdownSkeleton";
import ClickableTooltip from "../../../shared/components/toolTipComponent/ClickableTooltip";

const Orders = ({
  currentStore,
  spinnerArray,
  shopifyOrders,
  getShopifyOrders,
  updateShopifyOrderStatus,
}) => {
  //-------- Grid red
  const gridRef = useRef();
  const tooltipRef = useRef();

  //------- usestates
  const [orderDetail, setOrderDetail] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchText, setSearchText] = useState(EMPTY_STRING);
  const [gridState, setGridState] = useState(defaultGridState);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isShowOrderDetail, setIsShowOrderDetail] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState(EMPTY_STRING);
  const [orderType, setOrderType] = useState({
    label: globalConstants.ALL,
    value: globalConstants.ALL,
  });
  const [orderPaymentStatus, setOrderPaymentStatus] = useState({
    label: globalConstants.ALL,
    value: globalConstants.ALL,
  });
  const [orderFulfillmentStatus, setOrderFulfillmentStatus] = useState({
    label: globalConstants.ALL,
    value: globalConstants.ALL,
  });
  const [timeFrame, setTimeFrame] = useState({
    start: "",
    end: "",
    duration: dashboardConstants.DAILY,
  });

  function actionCellRenderer(params) {
    if (params.data?.id) {
      const actionButtons = getShopifyOrderActions(
        params?.data,
        currentStore,
        gridState,
        updateShopifyOrderStatus,
        handleMarkOrderAsOpen,
        handleMarkOrderAsReady,
        handleMarkOrderAsFulfilled,
        handleTransferToLocation
      );
      return (
        <div
          onMouseLeave={() => {
            tooltipRef.current?.close();
          }}
        >
          <button
            type="button"
            onClick={() => {
              setOrderDetail(params?.data);
              toggleOrderDetailModal();
            }}
            className={`action-Button-Wrapper view-order-icon`}
            disabled={false}
          >
            <ShowIcon className="cursor-pointer" />
          </button>

          <>
            {actionButtons.props?.children?.length > 0 && (
              <ClickableTooltip
                anchorSelect={`#tool${params.data.id}`}
                openOnClick={true}
                tooltipRef={tooltipRef}
              >
                {actionButtons}
              </ClickableTooltip>
            )}
            <button
              id={`tool${params.data.id}`}
              className={`action-Button-Wrapper ${
                actionButtons.props?.children?.length > 0
                  ? "cursor-pointer"
                  : "disabled-icon"
              }`}
              disabled={actionButtons.props?.children?.length <= 0}
              onClick={() => {
                tooltipRef.current?.isOpen
                  ? tooltipRef.current?.close()
                  : tooltipRef.current?.open({
                      anchorSelect: `tool${params.data.id}`,
                    });
              }}
            >
              <ActionDotsIcons
                className={
                  actionButtons.props?.children?.length > 0
                    ? "cursor-pointer"
                    : "disabled-icon"
                }
              />
            </button>
          </>
        </div>
      );
    } else {
      return <DropdownSkeleton width="5em" height="1.5em" />;
    }
  }

  //------- Columns for Orders Grid
  const OrdersColumns = useMemo(
    () => {
      const commonClassesForRowCell = (params) => `inventory-cell-max-size 
           ${handleIsShopifyOrderCancelled(params?.data, true)}
          `;
      return [
        {
          headerName: "Date",
          field: "createdOn",
          cellRenderer: (params) => {
            return params?.data?.createdOn ? (
              <TableCustomHeader
                id={`created-on-in-array-${params?.data?.id}`}
                label={
                  <span
                    className={`${commonClassesForRowCell(params)} 
                    shopify-cell-max-size`}
                  >
                    {getReceiptPrintDate(params?.data?.createdOn)}
                  </span>
                }
                tooltopText={
                  <p className="text-start fw-normal m-0">
                    {getReceiptPrintDate(params?.data?.createdOn)}
                  </p>
                }
                className="sku-tooltip"
              />
            ) : (
              <DropdownSkeleton width="5em" height="1.5em" />
            );
          },
          checkboxSelection: true,
          headerCheckboxSelection: true,
          maxWidth: 150,
        },
        {
          headerName: "Customer",
          field: "customer",
          cellRenderer: (params) => {
            return params?.data?.id ? (
              <span className={commonClassesForRowCell(params)}>
                {`${params?.data?.customer?.first_name || "-"} ${
                  params?.data?.customer?.last_name || EMPTY_STRING
                }`}
              </span>
            ) : (
              <DropdownSkeleton width="5em" height="1.5em" />
            );
          },
          maxWidth: 140,
        },
        {
          headerName: "Order Type",
          field: "orderType",
          cellRenderer: (params) => {
            return params?.data?.id ? (
              <span className={commonClassesForRowCell(params)}>
                {params?.data?.orderType}
              </span>
            ) : (
              <DropdownSkeleton width="5em" height="1.5em" />
            );
          },
          maxWidth: 150,
        },
        {
          headerName: "# of Items",
          field: "noOfItem",
          cellRenderer: (params) => {
            return params?.data?.line_items?.length ? (
              <span className={commonClassesForRowCell(params)}>
                {getTotalLineProductsAndQuantity(params?.data)?.totalQuantity}
              </span>
            ) : (
              <DropdownSkeleton width="5em" height="1.5em" />
            );
          },
          maxWidth: 150,
        },
        {
          headerName: "Order Total",
          field: "orderTotal",
          cellRenderer: (params) => {
            return params?.data?.current_total_price ? (
              <span className={commonClassesForRowCell(params)}>
                {`$${params?.data?.current_total_price}`}
              </span>
            ) : (
              <DropdownSkeleton width="5em" height="1.5em" />
            );
          },
          maxWidth: 150,
        },
        {
          headerName: "Payment Status",
          field: "orderPaymentStatus",
          cellRenderer: (params) => {
            return params?.data?.id ? (
              <span className="inventory-cell-max-size shopify-status-max-cell-size">
                {getShopifyOrderFulfillmentStatus(
                  params?.data?.orderPaymentStatus
                )}
              </span>
            ) : (
              <DropdownSkeleton width="5em" height="1.5em" />
            );
          },
          enableSorting: false,
        },
        {
          headerName: "Fulfillment Status",
          field: "orderFulfillmentStatus",
          cellRenderer: (params) => {
            return params?.data?.id ? (
              <span className="inventory-cell-max-size shopify-status-max-cell-size">
                {getShopifyOrderFulfillmentStatus(
                  params?.data?.orderFulfillmentStatus
                )}
              </span>
            ) : (
              <DropdownSkeleton width="5em" height="1.5em" />
            );
          },
          enableSorting: false,
        },
        {
          maxWidth: 150,
          headerName: "",
          field: "id",
          cellStyle: {
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            overflow: "visible",
          },
          lockPosition: "right",
          cellRenderer: actionCellRenderer,
          sortable: false,
        },
      ];
    },
    // eslint-disable-next-line
    [shopifyOrders]
  );

  //------- update grid state
  const updateGridState = (gridState) => {
    getShopifyOrders(currentStore?.id, gridState);
    setGridState(gridState);
  };

  //------- get timeframe
  const handleGetTimeFrame = (value) => {
    getTimeFrame(value, timeFrame, setTimeFrame);
  };

  //------- handleSearchInputChange
  const handleSearchInputChange = (value) => {
    setIsSearchLoading(true);
    setDebouncedSearchText(value);
    handleDebouncedSearch(value);
  };

  //--------handleDebouncedSearch
  const handleDebouncedSearch = useDebouncedCallback((value) => {
    // setSearchText(value);
    updateGridState({ ...gridState, searchQuery: value, pageIndex: 1 });
  }, 1000);

  //-------- handle clear search text
  const clearSearchText = () => {
    handleSearchInputChange("");
  };

  const toggleOrderDetailModal = () => {
    setIsShowOrderDetail((prev) => {
      prev && updateGridState(gridState);
      return !prev;
    });
  };

  //------- handle selection of rows on checkbox selection
  const onSelectionChanged = (data) => {
    const selectedNodes = data.api.getSelectedRows();
    setSelectedRows(selectedNodes);
  };

  //-------- function to get data for all teh orders
  const gridData = useMemo(() => {
    let filteredData = shopifyOrders?.data || [];
    const paginationData = shopifyOrders?.paginationData || {};
    setGridState({
      ...gridState,
      pageIndex: paginationData?.pageNumber || defaultGridState?.pageIndex,
      totalRecords:
        paginationData?.totalRecords || defaultGridState?.totalRecords,
      pageEndRecordNumber:
        paginationData?.pageEndRecordNumber ||
        defaultGridState?.pageEndRecordNumber,
      pageStartRecordNumber:
        paginationData?.pageStartRecordNumber ||
        defaultGridState?.pageStartRecordNumber,
    });
    setIsSearchLoading(false);
    setOrderDetail((prev) => {
      let update = {};
      if (prev?.id) {
        update = shopifyOrders?.data?.find((order) => order.id === prev.id);
      }
      return update;
    });
    return filteredData;
  }, [shopifyOrders]);

  //------- Update GridState on filters chnage or date type
  useEffect(() => {
    if (currentStore?.id && timeFrame?.start && timeFrame?.end) {
      updateGridState({
        ...gridState,
        filters: prepareFilterForShopifyOrdersCall(
          orderType,
          orderPaymentStatus,
          orderFulfillmentStatus,
          timeFrame.start,
          timeFrame.end
        ),
      });
    }
  }, [
    orderType,
    orderPaymentStatus,
    orderFulfillmentStatus,
    currentStore,
    timeFrame,
  ]);

  return (
    <>
      <Topbar title={"Online Orders"}></Topbar>
      <Row className="m-0">
        <Col md={11} className="d-flex align-items-center gap-3">
          <DashboardDatePicker label="Date" getTimeFrame={handleGetTimeFrame} />{" "}
          <CategoriesDropdown
            label="Order Type"
            options={shopifyConstants.ORDERS_TYPES}
            handleChange={(e) => {
              setOrderType(
                e == null
                  ? {
                      label: globalConstants.ALL,
                      value: globalConstants.ALL,
                    }
                  : e
              );
            }}
            noOptionsMessage="No Order Type Found"
            placeholder="Order Type"
            SelectIconImage={categoryDropdownConstants.TRANSACTION_ICON}
            className={"dropdown-max-width"}
            isClearable={false}
            defaultValue={{
              value: PRODUCT_TYPES_ENUMS.ALL,
              label: "All",
            }}
          />
          <CategoriesDropdown
            label="Payment Status"
            options={getShopifyOrderPaymentStatusOptions()}
            handleChange={(e) => {
              setOrderPaymentStatus(
                e == null
                  ? {
                      label: globalConstants.ALL,
                      value: globalConstants.ALL,
                    }
                  : e
              );
            }}
            noOptionsMessage="No Order Status Found"
            placeholder="Order Status"
            SelectIconImage={categoryDropdownConstants.TRANSACTION_ICON}
            className={"dropdown-max-width"}
            isClearable={false}
            defaultValue={{
              value: PRODUCT_TYPES_ENUMS.ALL,
              label: "All",
            }}
          />
          <CategoriesDropdown
            label="Fulfillment Status"
            options={getShopifyOrderFulfillmentStatusOptions()}
            handleChange={(e) => {
              setOrderFulfillmentStatus(
                e == null
                  ? {
                      label: globalConstants.ALL,
                      value: globalConstants.ALL,
                    }
                  : e
              );
            }}
            noOptionsMessage="No Order Status Found"
            placeholder="Order Status"
            SelectIconImage={categoryDropdownConstants.TRANSACTION_ICON}
            className={"dropdown-max-width"}
            isClearable={false}
            defaultValue={{
              value: PRODUCT_TYPES_ENUMS.ALL,
              label: "All",
            }}
          />
          <SearchField
            isFocus={true}
            onChange={(e) => handleSearchInputChange(e.target.value)}
            IconImage={SearchIcon}
            label="Search"
            placeHolder="Search Customer Name or Item"
            value={debouncedSearchText}
            CrossIcon={debouncedSearchText ? CrossIcon : ""}
            clearSearchText={clearSearchText}
            BlueBoxIcon={BlueBoxIcon}
            LoadingIndicator={
              isSearchLoading ||
              (isButtonDisable(
                spinnerArray,
                shopifyConstants.GET_SHOPIFY_ORDERS
              ) &&
                searchText)
                ? LoadingIndicator
                : false
            }
          />
        </Col>
        <Col
          md={1}
          className="d-flex justify-content-end align-items-end gap-3"
        >
          {selectedRows?.length > 0 && (
            <Button
              handleClick={() => {
                handlePrintShopifyPackingSlips(selectedRows, currentStore);
              }}
              IconImage={PrintIcon}
              className={"batch-delete-button"}
            />
          )}
          {/*
        //------- Need for future use
          <Button
            handleClick={() => {}}
            IconImage={EditIcon}
            className={"batch-delete-button"}
          /> */}
        </Col>
        <Col md={12} className="mt-3">
          <ServerPaginationTable
            colDefs={OrdersColumns}
            gridRef={gridRef}
            gridData={
              isSpinnerEnabled(
                spinnerArray,
                shopifyConstants.GET_SHOPIFY_ORDERS
              )
                ? skeletonArray
                : gridData
            }
            selectedRows={selectedRows}
            gridState={gridState}
            getRowId={getRowId}
            getRowStyle={getRowStyle}
            updateGridState={updateGridState}
            onSelectionChanged={onSelectionChanged}
            paginationPageSize={gridState.pageSize}
            pageSizeOptions={gridState.pageSizeOptions}
            isDisablePaginationActions={isSpinnerEnabled(
              spinnerArray,
              shopifyConstants.GET_SHOPIFY_ORDERS
            )}
            tableHeight={`calc(100vh - 220px)`}
            tableId={TABLE_IDS.ORDERS}
          />
        </Col>
      </Row>
      {isShowOrderDetail && (
        <OrderDetailsModal
          orderDetail={orderDetail}
          currentStore={currentStore}
          shopifyOrdersCallBackConfig={{
            orderDetail,
            isRefetchOrdersOnSave: true,
            gridState: gridState,
            callBackFuction: (order) => {
              setOrderDetail(order);
            },
          }}
          defaultGridState={gridState}
          isShowOrderDetail={isShowOrderDetail}
          handleMarkOrderAsOpen={handleMarkOrderAsOpen}
          toggleOrderDetailModal={toggleOrderDetailModal}
          handleMarkOrderAsReady={handleMarkOrderAsReady}
          updateShopifyOrderStatus={updateShopifyOrderStatus}
          handleMarkOrderAsFulfilled={handleMarkOrderAsFulfilled}
          handleTransferToLocation={handleTransferToLocation}
        />
      )}
    </>
  );
};

//-------Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  currentStore: state.store.currentStore,
  spinnerArray: state.system.spinnerArray,
  shopifyOrders: state.shopify.shopifyOrders,
  allCustomers: state.customer.allCustomers,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getShopifyOrders: (storeId, gridState) =>
    dispatch(shopifyActions.getShopifyOrders(storeId, gridState)),
  updateShopifyOrderStatus: (
    storeId,
    data,
    gridState,
    toggleOrderDetailsModal
  ) =>
    dispatch(
      shopifyActions.updateShopifyOrderStatus(
        storeId,
        data,
        gridState,
        toggleOrderDetailsModal
      )
    ),
});

//-------Export AddNewUser Component
export default connect(mapStateToProps, mapDispatchToProps)(Orders);
